// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xBaseTheme } from "@rugby/XDL/types";

import ModalPresentationPropsDefault from "@insite/mobius/Modal/presentationProps";
import get from "@insite/mobius/utilities/get";
import getColor from "@insite/mobius/utilities/getColor";
import { screen } from "@rugby/XDL/Helpers";
import colors from "@rugby/XDL/PreStyle/colors";
import { css } from "styled-components";

// It reads the SizeVariants as percentage value.
// We don't add SizeVariants to "modal - pre" style because some modals such as ErrorModal read the style from Modal mobius as "px" value.
export const modalSizeVariants = {
    small: 40,
    medium: 60,
    large: 95,
};

const textColor = get(colors, "common.toast.light");
const closeIconColor = get(colors, "secondary.main");

export const modalFooter = css`
    padding: 20px 40px;
    background-color: ${getColor("common.ui3")};
    display: flex;
    margin: -40px;
    margin-bottom: -20px;
    margin-top: 40px;
    justify-content: flex-end;
    flex-direction: row-reverse;

    button:not(:last-child) {
        margin-left: 10px;
        margin-right: 0;
    }

    ${screen("lte", "xs")`
        flex-direction: column-reverse;

        button {
            width: 100%;

            &:not(:last-child) {
                margin-left: 0;
                margin-top: 10px;
            }
        }
    `}
`;

export default {
    defaultProps: {
        ...ModalPresentationPropsDefault,
        mergeCss: true,
        closeButtonProps: {
            ...ModalPresentationPropsDefault?.closeButtonProps,
            mergeCss: true,
            css: css`
                position: absolute;
                top: 2px;
                right: 0px;
                padding: 0 8px;
                background: transparent;
                border-color: transparent;

                &,
                &&& span {
                    color: ${closeIconColor};
                }

                &:hover {
                    background: transparent;
                    border-color: transparent;

                    &&& span {
                        color: ${getColor("text.main")};
                    }
                }
            `,
        },
        cssOverrides: {
            modalBody: css`
                position: relative;
                max-width: ${modalSizeVariants.small}%;
            `,
            modalTitle: css`
                border: 0;
                padding: 30px 40px 20px 40px;
                font-size: 32px;
                line-height: 35px;
                color: ${textColor};
                background-color: ${getColor("common.white")};

                h4 {
                    margin: 0;
                    margin-bottom: 0;
                }
            `,
            modalContent: css`
                padding: 40px;
                color: ${textColor};
                background-color: ${getColor("common.white")};

                div[class^="GridItem"] {
                    align-items: center;
                }

                > div:last-child[class^="sc-"] {
                    ${modalFooter}
                }
            `,
            headlineTypography: css`
                @media only screen and (max-width: 426px) {
                    padding-top: 30px;
                }
            `,
        },
        closeButtonIconProps: {
            ...ModalPresentationPropsDefault?.closeButtonIconProps,
            mergeCss: true,
            color: "secondary",
        },
    },
} as xBaseTheme["modal"];
